/**
 * PracticeAdminPanel — per-practice quick admin for Medical venues
 * ─────────────────────────────────────────────────────────────────
 *
 * Surfaces the existing backend endpoints in a single panel:
 *   • PATCH /api/directory/medical/{slug}/admin/settings — color swatches
 *     + Calendly URL + custom lead text + CTA copy
 *   • PATCH /api/directory/restaurants/{slug}/admin/cta-toggles — show or
 *     hide the Quick Order / Reserve / Book Now hero CTAs
 *
 * URL: /admin/practice/:slug
 * For Medical practices only (other niches can use the generic restaurant admin).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Palette, Calendar, ToggleLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import useSearchFeaturesAudit from "@/lib/useSearchFeaturesAudit";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
// Curated palette presets — each sub-niche has 3 swatch variants
const PALETTES = {
  GP:                [{primary:"#0369a1",accent:"#06b6d4",ink:"#0c4a6e",surface:"#f0f9ff",label:"Clinical blue"},{primary:"#0d9488",accent:"#14b8a6",ink:"#134e4a",surface:"#f0fdfa",label:"Healing teal"},{primary:"#0891b2",accent:"#22d3ee",ink:"#164e63",surface:"#ecfeff",label:"Sky"}],
  Dentist:           [{primary:"#0f766e",accent:"#5eead4",ink:"#134e4a",surface:"#f0fdfa",label:"Mint"},{primary:"#06b6d4",accent:"#67e8f9",ink:"#155e75",surface:"#ecfeff",label:"Cool teal"},{primary:"#14b8a6",accent:"#5eead4",ink:"#0f766e",surface:"#f0fdfa",label:"Spearmint"}],
  Hospital:          [{primary:"#1e40af",accent:"#3b82f6",ink:"#1e3a8a",surface:"#eff6ff",label:"Hospital navy"},{primary:"#b91c1c",accent:"#ef4444",ink:"#7f1d1d",surface:"#fef2f2",label:"Emergency red"},{primary:"#0c4a6e",accent:"#0ea5e9",ink:"#082f49",surface:"#e0f2fe",label:"Authority"}],
  Pharmacy:          [{primary:"#16a34a",accent:"#22c55e",ink:"#14532d",surface:"#f0fdf4",label:"Pharmacy green"},{primary:"#15803d",accent:"#86efac",ink:"#14532d",surface:"#f0fdf4",label:"Apothecary"},{primary:"#65a30d",accent:"#a3e635",ink:"#365314",surface:"#f7fee7",label:"Wellness lime"}],
  Chiropractor:      [{primary:"#92400e",accent:"#f59e0b",ink:"#78350f",surface:"#fffbeb",label:"Warm amber"},{primary:"#9a3412",accent:"#f97316",ink:"#7c2d12",surface:"#fff7ed",label:"Strength orange"},{primary:"#5b21b6",accent:"#a78bfa",ink:"#4c1d95",surface:"#f5f3ff",label:"Aligned purple"}],
  Optometrist:       [{primary:"#4338ca",accent:"#818cf8",ink:"#312e81",surface:"#eef2ff",label:"Vision indigo"},{primary:"#7c3aed",accent:"#a78bfa",ink:"#5b21b6",surface:"#f5f3ff",label:"Lens violet"},{primary:"#0369a1",accent:"#38bdf8",ink:"#0c4a6e",surface:"#f0f9ff",label:"Crystal blue"}],
  "Mental Health":   [{primary:"#7c3aed",accent:"#c4b5fd",ink:"#5b21b6",surface:"#f5f3ff",label:"Calming lavender"},{primary:"#0d9488",accent:"#5eead4",ink:"#134e4a",surface:"#f0fdfa",label:"Restorative teal"},{primary:"#db2777",accent:"#f9a8d4",ink:"#9d174d",surface:"#fdf2f8",label:"Empathy rose"}],
  "Sports Medicine": [{primary:"#262626",accent:"#f97316",ink:"#171717",surface:"#fafafa",label:"Performance charcoal"},{primary:"#dc2626",accent:"#fbbf24",ink:"#7f1d1d",surface:"#fffbeb",label:"Energy red"},{primary:"#1d4ed8",accent:"#60a5fa",ink:"#1e3a8a",surface:"#eff6ff",label:"Athletic blue"}],
  Podiatrist:        [{primary:"#475569",accent:"#94a3b8",ink:"#1e293b",surface:"#f8fafc",label:"Slate"},{primary:"#65a30d",accent:"#bef264",ink:"#365314",surface:"#f7fee7",label:"Ground lime"},{primary:"#9333ea",accent:"#c084fc",ink:"#6b21a8",surface:"#faf5ff",label:"Posture purple"}],
  Paediatric:        [{primary:"#0ea5e9",accent:"#fde047",ink:"#075985",surface:"#f0f9ff",label:"Pastel sky"},{primary:"#f472b6",accent:"#fbcfe8",ink:"#9d174d",surface:"#fdf2f8",label:"Bubblegum pink"},{primary:"#a3e635",accent:"#fef08a",ink:"#365314",surface:"#f7fee7",label:"Playful lime"}],
  Maternity:         [{primary:"#f43f5e",accent:"#fda4af",ink:"#9f1239",surface:"#fff1f2",label:"Blush"},{primary:"#ec4899",accent:"#fbcfe8",ink:"#831843",surface:"#fdf2f8",label:"Soft rose"},{primary:"#a855f7",accent:"#d8b4fe",ink:"#6b21a8",surface:"#faf5ff",label:"Maternal lilac"}],
  Clinic:            [{primary:"#0369a1",accent:"#38bdf8",ink:"#0c4a6e",surface:"#f0f9ff",label:"Clinical blue"},{primary:"#475569",accent:"#94a3b8",ink:"#1e293b",surface:"#f8fafc",label:"Professional slate"},{primary:"#0d9488",accent:"#5eead4",ink:"#134e4a",surface:"#f0fdfa",label:"Restorative teal"}],
};

export default function PracticeAdminPanel() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const [showReserve, setShowReserve] = useState(false);
  const [showBookNow, setShowBookNow] = useState(false);
  const [customLead, setCustomLead] = useState("");
  const [customCta, setCustomCta] = useState("");
  const { auditAfterSave } = useSearchFeaturesAudit();

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/directory/restaurants/${slug}`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        setCalendlyUrl(d.calendly_url || "");
        setShowQuickOrder(!!d.show_quick_order);
        setShowReserve(!!d.show_reserve_table);
        setShowBookNow(!!d.show_book_now);
        setCustomLead(d.medical_template?.lead || "");
        setCustomCta(d.medical_template?.cta || "");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  async function applyPalette(p) {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/directory/medical/${slug}/admin/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          primary: p.primary, accent: p.accent, ink: p.ink, surface: p.surface,
        }),
      });
      if (!r.ok) throw new Error("Couldn't apply palette");
      const j = await r.json();
      setData((d) => ({ ...d, medical_template: j.medical_template }));
      toast.success(`Palette applied: ${p.label}`);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function saveCalendly() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/directory/medical/${slug}/admin/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ calendly_url: calendlyUrl }),
      });
      if (!r.ok) throw new Error("Invalid Calendly URL");
      toast.success("Calendly URL saved");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function saveCopy() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/directory/medical/${slug}/admin/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead: customLead, cta: customCta }),
      });
      if (!r.ok) throw new Error("Couldn't save copy");
      toast.success("Hero copy updated");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function saveToggles() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${slug}/admin/cta-toggles`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          show_quick_order: showQuickOrder,
          show_reserve_table: showReserve,
          show_book_now: showBookNow,
        }),
      });
      if (!r.ok) throw new Error("Couldn't save toggles");
      toast.success("CTA toggles saved");
      // Surface any post-save search-feature recommendations
      auditAfterSave(slug);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-stone-500">Practice not found.</div>;

  const tpl = data.medical_template || {};
  const isMedical = data.niche === "Medical";
  const palettes = isMedical ? (PALETTES[data.sub_niche] || PALETTES.Clinic) : [];

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-30 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to={`/r/${slug}`} className="p-2 -ml-2 rounded-lg hover:bg-stone-100" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold text-stone-500">Quick admin</p>
            <p className="font-semibold text-stone-900 truncate">{data.name}</p>
          </div>
          <Badge n={data.sub_niche || data.cuisine || ""} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Palette */}
        {isMedical && (
          <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="admin-palette-section">
            <h2 className="text-lg font-bold text-stone-900 flex items-center gap-2 mb-1">
              <Palette className="w-4 h-4" /> Color palette
            </h2>
            <p className="text-[13px] text-stone-600 mb-4">
              Choose a palette appropriate for <strong>{data.sub_niche}</strong> — applies across the entire practice site.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {palettes.map((p) => {
                const active = p.primary === tpl.primary;
                return (
                  <button
                    key={p.label}
                    data-testid={`palette-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    disabled={saving}
                    onClick={() => applyPalette(p)}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-sm ${active ? "border-stone-900 shadow-md" : "border-stone-200"}`}
                  >
                    <div className="flex items-center gap-1.5 mb-2">
                      {[p.primary, p.accent, p.ink, p.surface].map((c, i) => (
                        <div key={i} className="w-6 h-6 rounded-md border border-stone-200" style={{ background: c }} />
                      ))}
                    </div>
                    <p className="font-semibold text-[13px] text-stone-900">{p.label}</p>
                    {active && <p className="text-[11px] text-emerald-600 font-bold mt-0.5">✓ Active</p>}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Calendly */}
        {isMedical && (
          <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="admin-calendly-section">
            <h2 className="text-lg font-bold text-stone-900 flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4" /> Calendly link
            </h2>
            <p className="text-[13px] text-stone-600 mb-4">
              When set, patients can also "Schedule via Calendly" from the booking modal.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="admin-calendly-input"
                value={calendlyUrl}
                onChange={(e) => setCalendlyUrl(e.target.value)}
                placeholder="https://calendly.com/your-practice/15min"
                className="flex-1"
              />
              <Button data-testid="admin-calendly-save" onClick={saveCalendly} disabled={saving}>
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save
              </Button>
            </div>
          </section>
        )}

        {/* Custom copy */}
        {isMedical && (
          <section className="bg-white rounded-2xl border border-stone-200 p-5">
            <h2 className="text-lg font-bold text-stone-900 mb-1">Hero copy</h2>
            <p className="text-[13px] text-stone-600 mb-4">Override the default tagline + CTA button text.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-[12px] font-semibold">Lead text (1-2 lines)</Label>
                <Input
                  data-testid="admin-lead-input"
                  value={customLead}
                  onChange={(e) => setCustomLead(e.target.value)}
                  placeholder="Your trusted local GP. Bulk-billing eligible."
                />
              </div>
              <div>
                <Label className="text-[12px] font-semibold">CTA button text</Label>
                <Input
                  data-testid="admin-cta-input"
                  value={customCta}
                  onChange={(e) => setCustomCta(e.target.value)}
                  placeholder="Book appointment"
                />
              </div>
              <Button data-testid="admin-copy-save" onClick={saveCopy} disabled={saving}>
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save copy
              </Button>
            </div>
          </section>
        )}

        {/* CTA toggles */}
        <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="admin-cta-section">
          <h2 className="text-lg font-bold text-stone-900 flex items-center gap-2 mb-1">
            <ToggleLeft className="w-4 h-4" /> Hero CTAs
          </h2>
          <p className="text-[13px] text-stone-600 mb-4">
            Control which buttons appear in the site header. When two are on, they auto-pair into a smaller toggle.
          </p>
          <div className="space-y-3">
            <ToggleRow label="Quick Order" hint="Pickup / Delivery / Eat-in flow at the top of the menu" value={showQuickOrder} onChange={setShowQuickOrder} testid="admin-toggle-qo" />
            <ToggleRow label="Reserve a Table" hint="Dinner reservation flow with date / party-size" value={showReserve} onChange={setShowReserve} testid="admin-toggle-reserve" />
            <ToggleRow label="Book Now" hint="Appointment booking (Medical only)" value={showBookNow} onChange={setShowBookNow} testid="admin-toggle-book" />
          </div>
          <Button data-testid="admin-toggles-save" onClick={saveToggles} disabled={saving} className="mt-4">
            <Save className="w-3.5 h-3.5 mr-1.5" /> Save toggles
          </Button>
        </section>

        <div className="text-center pt-4">
          <Link to={`/r/${slug}`} className="text-[13px] text-stone-600 underline" data-testid="admin-preview-link">
            ← Preview live site
          </Link>
        </div>
      </main>
    </div>
  );
}

function ToggleRow({ label, hint, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[14px] text-stone-900">{label}</p>
        <p className="text-[11px] text-stone-500">{hint}</p>
      </div>
      <Switch
        data-testid={testid}
        checked={value}
        onCheckedChange={onChange}
      />
    </div>
  );
}

function Badge({ n }) {
  if (!n) return null;
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-stone-100 text-stone-700">{n}</span>
  );
}
